import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EVENTS_KEEP_LIMIT, EVENTS_POLL_MS } from '../constants';
import { useCoreServiceClient } from '../api/coreServiceClient';
import type { CoreEventDto } from '../types';

type CoreEventsContextValue = {
  /** Newest first. */
  events: CoreEventDto[];
  /** Sequence number of the newest event seen, 0 before the first load. */
  lastSeq: number;
  isPaused: boolean;
  hasError: boolean;
  setPaused: (value: boolean) => void;
  clear: () => void;
};

const CoreEventsContext = createContext<CoreEventsContextValue>({
  events: [],
  lastSeq: 0,
  isPaused: false,
  hasError: false,
  setPaused: () => undefined,
  clear: () => undefined,
});

export const useCoreEvents = () => useContext(CoreEventsContext);

/**
 * Follows the event log of the core service by polling `GET /events?after=<seq>`.
 * (The SSE stream cannot be used here: the browser EventSource cannot send the Authorization header.)
 */
export const CoreEventsProvider = ({ children }: { children: ReactNode }) => {
  const client = useCoreServiceClient();
  const [events, setEvents] = useState<CoreEventDto[]>([]);
  const [lastSeq, setLastSeq] = useState(0);
  const [isPaused, setPaused] = useState(false);
  const [hasError, setHasError] = useState(false);
  const lastSeqRef = useRef(0);

  const clear = useCallback(() => setEvents([]), []);

  useEffect(() => {
    if (isPaused) {
      return undefined;
    }

    let isActive = true;
    let timer: ReturnType<typeof setTimeout>;

    const accept = (incoming: CoreEventDto[]) => {
      if (!incoming.length) {
        return;
      }

      const newest = Math.max(...incoming.map(event => event.id));

      lastSeqRef.current = Math.max(lastSeqRef.current, newest);
      setLastSeq(lastSeqRef.current);
      setEvents(previous => {
        const known = new Set(previous.map(event => event.id));
        const fresh = incoming.filter(event => !known.has(event.id)).sort((a, b) => b.id - a.id);

        return [...fresh, ...previous].slice(0, EVENTS_KEEP_LIMIT);
      });
    };

    const poll = async () => {
      try {
        const incoming = lastSeqRef.current
          ? await client.getEventsAfter(lastSeqRef.current, 200)
          : await client.getRecentEvents(100);

        if (isActive) {
          accept(incoming);
          setHasError(false);
        }
      } catch (error) {
        if (isActive) {
          setHasError(true);
        }
      }

      if (isActive) {
        timer = setTimeout(poll, EVENTS_POLL_MS);
      }
    };

    poll();

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [client, isPaused]);

  const value = useMemo(
    () => ({ events, lastSeq, isPaused, hasError, setPaused, clear }),
    [events, lastSeq, isPaused, hasError, clear],
  );

  return <CoreEventsContext.Provider value={value}>{children}</CoreEventsContext.Provider>;
};
